import { DataPacket_Kind, Room, RoomEvent } from "livekit-client";
import { useEffect, useRef, useState } from "react";

export default function RagTestUI() {
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [availableModels, setAvailableModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState("");
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [newProjectName, setNewProjectName] = useState("");
    const [file, setFile] = useState(null);
    const [uploadMessage, setUploadMessage] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    const roomRef = useRef(null);
    const messagesEndRef = useRef(null);
    const conversationIdRef = useRef(null);

    const backend = process.env.REACT_APP_BACKEND_URL || "https://backend.modeai.io";
    const token = localStorage.getItem("accessToken") || "";

    // Fetch models and projects on mount
    useEffect(() => {
        async function fetchData() {
            try {
                // Fetch Models
                const modelRes = await fetch(`${backend}/ai/models/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (modelRes.ok) {
                    const data = await modelRes.json();
                    setAvailableModels(data.text_models || []);
                    if (data.text_models?.length > 0) {
                        setSelectedModel(data.text_models[0].model_name);
                    }
                }

                // Fetch Projects
                const projectRes = await fetch(`${backend}/chat/projects/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (projectRes.ok) {
                    const data = await projectRes.json();
                    setProjects(data.projects || []);
                }
            } catch (e) {
                console.error("Failed to fetch startup data:", e);
            }
        }
        fetchData();
    }, [backend, token]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) return;
        try {
            const res = await fetch(`${backend}/chat/projects/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ name: newProjectName }),
            });
            if (res.ok) {
                const data = await res.json();
                setProjects([...projects, data]);
                setSelectedProjectId(data.id);
                setNewProjectName("");
                alert("Project created!");
            } else {
                const errorData = await res.json();
                alert(`Failed to create project: ${errorData.detail || res.statusText}`);
            }
        } catch (e) {
            console.error("Failed to create project:", e);
            alert("Failed to create project due to network error.");
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setUploadMessage("Please select a file first.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        if (selectedProjectId) {
            formData.append("project_id", selectedProjectId);
        }

        try {
            setIsUploading(true);
            const res = await fetch(`${backend}/knowledge-base/upload-pdf/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (res.ok) {
                setUploadMessage("Upload successful! Document is being indexed.");
                setFile(null);
            } else {
                const data = await res.json();
                setUploadMessage(data.error || "Upload failed!");
            }
        } catch (err) {
            setUploadMessage("Error uploading file.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleConnect = async () => {
        if (!selectedModel || connecting) return;
        setConnecting(true);

        try {
            const res = await fetch(`${backend}/chat/session/start/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    model: selectedModel,
                    project_id: selectedProjectId || null
                }),
            });

            if (!res.ok) {
                setConnecting(false);
                return;
            }

            const data = await res.json();
            conversationIdRef.current = data.conversation_id;

            const { url, token: livekitToken } = data.livekit || {};
            const room = new Room();
            roomRef.current = room;

            room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
                if (topic !== "chat:text" && topic !== "chat") return;
                let decoded;
                try {
                    decoded = JSON.parse(new TextDecoder().decode(payload));
                } catch (e) { return; }

                if (decoded.type === "assistant_delta") {
                    setMessages((prev) => {
                        const last = prev[prev.length - 1];
                        if (last?.role === "assistant") {
                            return [...prev.slice(0, -1), { ...last, text: last.text + decoded.delta }];
                        }
                        return [...prev, { role: "assistant", text: decoded.delta }];
                    });
                } else if (decoded.type === "assistant_final") {
                    setMessages((prev) => {
                        const last = prev[prev.length - 1];
                        const updated = {
                            role: "assistant",
                            text: decoded.text,
                            model_display_name: decoded.model_display_name,
                            rag_enabled: decoded.rag_enabled,
                            project_name: decoded.project
                        };
                        if (last?.role === "assistant") {
                            return [...prev.slice(0, -1), updated];
                        }
                        return [...prev, updated];
                    });
                }
            });

            room.on(RoomEvent.Connected, () => {
                setConnected(true);
                setConnecting(false);
            });

            room.on(RoomEvent.Disconnected, () => {
                setConnected(false);
            });

            await room.connect(url, livekitToken);
        } catch (e) {
            setConnecting(false);
        }
    };

    const handleDisconnect = () => {
        roomRef.current?.disconnect();
        setConnected(false);
        setMessages([]);
    };

    const handleSend = async () => {
        const text = message.trim();
        if (!text || !roomRef.current?.localParticipant) return;

        setMessages((prev) => [...prev, { role: "user", text }]);
        setMessage("");

        // Save to DB
        try {
            await fetch(`${backend}/chat/message/save/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    conversation_id: conversationIdRef.current,
                    role: "user",
                    content: text,
                    selected_model: selectedModel,
                }),
            });
        } catch (e) { }

        // Send to LiveKit
        const payload = new TextEncoder().encode(JSON.stringify({ type: "user_msg", text, selected_model: selectedModel }));
        roomRef.current.localParticipant.publishData(payload, { reliable: true, topic: "chat:text" });
    };

    return (
        <div style={styles.container}>
            <div style={styles.sidebar}>
                <h3>RAG Settings</h3>
                <label style={styles.label}>Select Project:</label>
                <select
                    style={styles.select}
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    disabled={connected}
                >
                    <option value="">Standalone (No RAG)</option>
                    {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>

                <div style={styles.divider} />

                <label style={styles.label}>Create New Project:</label>
                <div style={{ display: "flex", gap: "5px", marginTop: "5px" }}>
                    <input
                        style={{ ...styles.input, padding: "5px" }}
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        placeholder="Project name"
                    />
                    <button style={styles.smallBtn} onClick={handleCreateProject}>Create</button>
                </div>

                <div style={styles.divider} />

                <label style={styles.label}>Upload Knowledge Base:</label>
                <div style={{ marginTop: "5px" }}>
                    <input
                        type="file"
                        onChange={(e) => setFile(e.target.files[0])}
                        style={{ fontSize: "11px", width: "100%" }}
                    />
                    <button
                        style={{ ...styles.connectBtn, marginTop: "10px", background: "#4b5563" }}
                        onClick={handleUpload}
                        disabled={isUploading || !selectedProjectId}
                    >
                        {isUploading ? "Uploading..." : "Upload Document"}
                    </button>
                    {uploadMessage && <div style={{ fontSize: "10px", color: "#3b82f6", marginTop: "5px" }}>{uploadMessage}</div>}
                </div>

                <div style={styles.divider} />

                <label style={styles.label}>Select Model:</label>
                <select
                    style={styles.select}
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    disabled={connected}
                >
                    {availableModels.map(m => (
                        <option key={m.id} value={m.model_name}>{m.display_name || m.model_name}</option>
                    ))}
                </select>

                {!connected ? (
                    <button style={styles.connectBtn} onClick={handleConnect} disabled={connecting}>
                        {connecting ? "Connecting..." : "Connect to LiveKit"}
                    </button>
                ) : (
                    <button style={styles.disconnectBtn} onClick={handleDisconnect}>Disconnect</button>
                )}
            </div>

            <div style={styles.chatArea}>
                <div style={styles.messages}>
                    {messages.length === 0 && (
                        <div style={styles.emptyState}>
                            Connect and send a message to test RAG context.
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} style={m.role === "user" ? styles.userMsg : styles.assistantMsg}>
                            <div style={styles.msgHeader}>
                                {m.role === "user" ? "You" : `Assistant (${m.model_display_name || "AI"})`}
                                {m.rag_enabled && <span style={styles.ragBadge}>✓ RAG: {m.project_name}</span>}
                            </div>
                            <div>{m.text}</div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                <div style={styles.inputArea}>
                    <input
                        style={styles.input}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={connected ? "Ask a question about your project documents..." : "Connect first"}
                        disabled={!connected}
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    />
                    <button style={styles.sendBtn} onClick={handleSend} disabled={!connected || !message.trim()}>Send</button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: { display: "flex", height: "100vh", fontFamily: "sans-serif" },
    sidebar: { width: "300px", padding: "20px", borderRight: "1px solid #ddd", background: "#f9f9f9" },
    chatArea: { flex: 1, display: "flex", flexDirection: "column", background: "#fff" },
    label: { display: "block", marginTop: "15px", fontSize: "12px", color: "#666" },
    select: { width: "100%", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ccc" },
    connectBtn: { width: "100%", marginTop: "20px", padding: "10px", background: "#10b981", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" },
    disconnectBtn: { width: "100%", marginTop: "20px", padding: "10px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" },
    messages: { flex: 1, padding: "20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px" },
    emptyState: { textAlign: "center", color: "#aaa", marginTop: "100px" },
    userMsg: { alignSelf: "flex-end", background: "#dbeafe", padding: "10px", borderRadius: "8px", maxWidth: "80%" },
    assistantMsg: { alignSelf: "flex-start", background: "#f3f4f6", padding: "10px", borderRadius: "8px", maxWidth: "80%" },
    msgHeader: { fontSize: "11px", color: "#666", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" },
    divider: { height: "1px", background: "#ddd", margin: "20px 0" },
    smallBtn: { padding: "5px 10px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "4px", fontSize: "12px", cursor: "pointer" },
    ragBadge: { background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: "4px", fontWeight: "bold" },
    inputArea: { padding: "20px", borderTop: "1px solid #ddd", display: "flex", gap: "10px" },
    input: { flex: 1, padding: "10px", borderRadius: "4px", border: "1px solid #ccc" },
    sendBtn: { padding: "10px 20px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }
};
